import { useRef, type ReactNode } from 'react';
import { useMedia, formatBytes } from '../media.js';
import { useSettings } from '../settings.js';
import type { StringKey } from '../i18n/index.js';

/**
 * Pictures and recordings a teacher supplies.
 *
 * Two kinds, one shelf, because they need exactly the same three things: an
 * upload with a ceiling, a listing, and a delete. Pictures go under puzzle
 * boards — every one of them joins the "a different one each board" draw —
 * and recordings replace the synthesised praise for this teacher's classes.
 *
 * Without an account, pictures are kept in this browser instead and the
 * recordings shelf is not shown: a recording only matters to a class, and a
 * class needs an account.
 */
export function MediaManager() {
  const media = useMedia();
  const { t, num } = useSettings();
  const pictureInput = useRef<HTMLInputElement>(null);
  const voiceInput = useRef<HTMLInputElement>(null);
  const voices = media.assets.filter((a) => a.kind === 'voice');

  const del = (id: string) => { if (window.confirm(t('media.deleteAsk'))) void media.remove(id); };

  return (
    <div className="media">
      {media.available ? (
        <p className="a-empty">
          {t('media.usage', { a: num(formatBytes(media.usedBytes)), b: num(formatBytes(media.quotaBytes)) })}
        </p>
      ) : (
        <p className="a-hint">{t('media.localOnly')}</p>
      )}
      {media.error && (
        <p className="a-error">{t(`media.err.${media.error}` as StringKey)}</p>
      )}

      <Shelf
        title={t('media.pictures')} help={t('media.pictureHelp')}
        input={pictureInput} accept="image/png,image/jpeg,image/webp,image/gif"
        onFile={(f) => void media.upload('picture', f)}
        empty={media.pictures.length === 0}
      >
        {media.pictures.map((p) => (
          <li key={p.id}>
            <img className="media-thumb" src={p.src} alt="" />
            <span className="media-name">
              {p.name}
              {/* Signed in, a picture kept in this browser before is still
                  offered — but it will not reach the class, so it says so. */}
              {p.local && media.available && <small className="media-local">{t('media.thisBrowser')}</small>}
            </span>
            <span className="media-size">{num(formatBytes(p.bytes))}</span>
            <button type="button" className="a-reset" onClick={() => del(p.id)}>{t('admin.delete')}</button>
          </li>
        ))}
      </Shelf>

      {media.available && (
        <Shelf
          title={t('media.voices')} help={t('media.voiceHelp')}
          input={voiceInput} accept="audio/*"
          onFile={(f) => void media.upload('voice', f)}
          empty={voices.length === 0}
        >
          {voices.map((a) => (
            <li key={a.id}>
              <audio className="media-audio" controls preload="none" src={`/api/assets/${a.id}/raw`} />
              <span className="media-name">{a.name}</span>
              <span className="media-size">{num(formatBytes(a.bytes))}</span>
              <button type="button" className="a-reset" onClick={() => del(a.id)}>{t('admin.delete')}</button>
            </li>
          ))}
        </Shelf>
      )}
    </div>
  );

  function Shelf({ title, help, input, accept, onFile, empty, children }: {
    title: string;
    help: string;
    input: React.RefObject<HTMLInputElement>;
    accept: string;
    onFile: (f: File) => void;
    empty: boolean;
    children: ReactNode;
  }) {
    return (
      <div className="media-shelf">
        <div className="media-hd">
          <b>{title}</b>
          <button
            className="a-reset" type="button" disabled={media.busy}
            onClick={() => input.current?.click()}
          >
            {media.busy ? t('media.uploading') : t('media.upload')}
          </button>
        </div>
        <p className="a-hint">{help}</p>
        <input
          ref={input} type="file" accept={accept} hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            // Reset first: picking the same file twice must fire again.
            e.target.value = '';
            if (file) onFile(file);
          }}
        />
        {empty ? <p className="a-empty">{t('media.none')}</p> : <ul className="media-list">{children}</ul>}
      </div>
    );
  }
}
